import React, { useState, useEffect } from 'react';
import { Edit, DollarSign, ExternalLink, Mail, MessageSquare, X, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import ItemizedInvoicesView from './itemizedInvoiceView';
import { InvoiceProjectCustomer } from './invoiceType';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import { formatAddress, formatCurrency, formatDate } from '../lib/global_function';

interface InvoicesTableProps {
    invoices: InvoiceProjectCustomer[];
    onEdit: (invoiceId: string) => void;
    onAddPayment: (invoiceId: string) => void;
    onDelete: (invoiceId: string) => void;
    onSendToCustomer: (invoice: InvoiceProjectCustomer) => void;
    onSendToQuickbooks: (invoice: InvoiceProjectCustomer) => void;
    onText: (invoice: InvoiceProjectCustomer, invoice_id: string) => void;
    onView: (invoiceId: string) => void;
}

const InvoicesTableView: React.FC<InvoicesTableProps> = ({
    invoices,
    onEdit,
    onAddPayment,
    onDelete,
    onSendToCustomer,
    onSendToQuickbooks,
    onText,
    onView,
}) => {
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
    const [sortedInvoices, setSortedInvoices] = useState<InvoiceProjectCustomer[]>(invoices);
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        setSortedInvoices(invoices);
    }, [invoices]);

    const toggleRow = (invoiceId: string) => {
        setExpandedRows((prev) => ({
            ...prev,
            [invoiceId]: !prev[invoiceId],
        }));
    };

    const clearSelection = () => {
        setSelectedInvoiceId(null);
    };

    const sortByProjectName = (ascending: boolean) => {
        const sorted = [...invoices].sort((a, b) => {
            const nameA = a.project_name.toLowerCase();
            const nameB = b.project_name.toLowerCase();
            return ascending ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        });
        setSortedInvoices(sorted);
    };

    const sortByCustomerLastName = (ascending: boolean) => {
        const sorted = [...invoices].sort((a, b) => {
            const nameA = a.customer_last_name.toLowerCase();
            const nameB = b.customer_last_name.toLowerCase();
            return ascending ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        });
        setSortedInvoices(sorted);
    };

    const handleEdit = () => {
        if (selectedInvoiceId) onEdit(selectedInvoiceId);
    };

    const handleAddPayment = () => {
        if (selectedInvoiceId) onAddPayment(selectedInvoiceId);
    };

    const handleDelete = () => {
        if (selectedInvoiceId) onDelete(selectedInvoiceId);
    };

    const handleView = () => {
        if (selectedInvoiceId) {
            onView(selectedInvoiceId);
        }
    };

    const handleSendToCustomer = () => {
        const invoice = invoices.find((e) => e.invoice_id === selectedInvoiceId);
        if (invoice) {
            onSendToCustomer(invoice);
        } else {
            setErrorMessage('Invoice not found.');
        }
    };

    const handleSendToQuickbooks = () => {
        const invoice = invoices.find((e) => e.invoice_id === selectedInvoiceId);
        if (invoice) {
            onSendToQuickbooks(invoice);
        } else {
            setErrorMessage('Invoice not found.');
        }
    };

//    const formatDate = (dateString: string) => {
//        if (!dateString) return '';
//        try {
//            return new Date(dateString).toLocaleDateString();
//        } catch {
//            return dateString;
//        }
//    };

    return (
        <div className="space-y-6">
            {/* Error Message */}
            {errorMessage && (
                <Alert variant="error">
                    {errorMessage}
                </Alert>
            )}

            {/* Sorting Controls */}
            <div className="flex justify-between items-center bg-gray-100 p-3 rounded">
                <div className="flex gap-6 items-center flex-wrap">
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground">Sort by Project:</span>
                        <button
                            onClick={() => sortByProjectName(true)}
                            className="hover:bg-gray-200 p-1 rounded"
                            title="Sort by Project Name Ascending"
                        >
                            <ChevronUp className="size-4" />
                        </button>
                        <button
                            onClick={() => sortByProjectName(false)}
                            className="hover:bg-gray-200 p-1 rounded"
                            title="Sort by Project Name Descending"
                        >
                            <ChevronDown className="size-4" />
                        </button>
                    </div>
                    {/** */}
                    <span className="text-gray-400">||</span>

                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground">Sort by Customer Last Name:</span>
                        <button
                            onClick={() => sortByCustomerLastName(true)}
                            className="hover:bg-gray-200 p-1 rounded"
                            title="Sort by Customer Last Name Ascending"
                        >
                            <ChevronUp className="size-4" />
                        </button>
                        <button
                            onClick={() => sortByCustomerLastName(false)}
                            className="hover:bg-gray-200 p-1 rounded"
                            title="Sort by Customer Last Name Descending"
                        >
                            <ChevronDown className="size-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Action Bar */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg flex flex-wrap items-center gap-3 text-sm">
                <div className="flex items-center gap-2">
                    {selectedInvoiceId ? (
                        <>
                            <button
                                onClick={clearSelection}
                                className="text-gray-600 hover:text-gray-900"
                                title="Clear Selection"
                            >
                                <X className="size-5" />
                            </button>
                            <Badge variant="default">1 invoice selected</Badge>
                        </>
                    ) : (
                        <span className="text-gray-500">No invoice selected</span>
                    )}
                </div>

                <span className="text-gray-300">|</span>

                <button
                    onClick={handleEdit}
                    disabled={!selectedInvoiceId}
                    className={`flex items-center gap-1.5 ${
                        selectedInvoiceId
                            ? 'text-primary hover:text-primary-hover cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed'
                    }`}
                    title={selectedInvoiceId ? "Edit Invoice" : "Select an invoice to edit"}
                >
                    <Edit className="size-4" /> Edit
                </button>

{/**
                <span className="text-gray-300">|</span>
*/}

                <button
                    onClick={handleView}
                    disabled={!selectedInvoiceId}
                    className={`flex items-center gap-1.5 ${
                        selectedInvoiceId
                            ? 'text-primary hover:text-primary-hover cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed'
                    }`}
                    title={selectedInvoiceId ? "View Invoice PDF" : "Select an invoice to view"}
                >
                    <FileText className="size-4" /> PDF View
                </button>

{/**
                <span className="text-gray-300">|</span>
*/}
                
                <button
                    onClick={handleAddPayment}
                    disabled={!selectedInvoiceId}
                    className={`flex items-center gap-1.5 ${
                        selectedInvoiceId
                            ? 'text-primary hover:text-primary-hover cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed'
                    }`}
                    title={selectedInvoiceId ? "Add Payment" : "Select an invoice to add payment"}
                >
                    <DollarSign className="size-4" /> Add Payment
                </button>

{/**
                <span className="text-gray-300">|</span>
*/}



                <button
                    onClick={handleSendToCustomer}
                    disabled={!selectedInvoiceId}
                    className={`flex items-center gap-1.5 ${
                        selectedInvoiceId
                            ? 'text-primary hover:text-primary-hover cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed'
                    }`}
                    title={selectedInvoiceId ? "Send Invoice" : "Select an invoice to send"}
                >
                    <Mail className="size-4" /> <MessageSquare className="size-4" /> Send to Customer
                </button>

{/**
                <span className="text-gray-300">|</span>
*/}

                <button
                    onClick={handleSendToQuickbooks}
                    disabled={!selectedInvoiceId}
                    className={`flex items-center gap-1.5 ${
                        selectedInvoiceId
                            ? 'text-primary hover:text-primary-hover cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed'
                    }`}
                    title={selectedInvoiceId ? "Send to QuickBooks" : "Select an invoice to send to QuickBooks"}
                >
                    <ExternalLink className="size-4" /> Send to Quickbooks
                </button>
            </div>

            {/* Invoice List */}
            {sortedInvoices.map((invoice) => {
                const balanceDue = Number(invoice.final_total) - Number(invoice.amount_paid);
                const discountAmount = Number(invoice.discount_usd) || 0;

                return (
                    <div
                        key={invoice.invoice_id}
                        className="border border-gray-300 rounded p-4 space-y-3"
                    >
                        {/* Project and Customer Info */}
                        <div className="ml-6">
                            <div className="mb-2">
                                <strong className="font-semibold text-foreground">Project:</strong>
                                <span className="ml-2 text-sm text-gray-700">
                                    {invoice.project_name} - {invoice.project_type} -{' '}
                                    {(invoice.project_description || '').length > 75
                                        ? `${invoice.project_description.slice(0, 75)}...`
                                        : invoice.project_description || ''}
                                </span>
                            </div>
                            <div>
                                <strong className="font-semibold text-foreground">Customer:</strong>
                                <span className="ml-2 text-sm text-gray-700">
                                    {invoice.customer_first_name} {invoice.customer_last_name},{' '}
                                    {formatAddress(
                                        invoice.customer_address_1,
                                        '',
                                        invoice.customer_city,
                                        invoice.customer_state,
                                        invoice.customer_zip
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Invoice Details */}
                        <div className="flex items-start gap-4 bg-gray-100 p-3 rounded">
                            <input
                                type="radio"
                                name="invoiceSelection"
                                checked={selectedInvoiceId === invoice.invoice_id}
                                onChange={() => setSelectedInvoiceId(invoice.invoice_id)}
                                className="mt-1"
                                style={{ accentColor: 'rgb(248, 98, 3)' }}
                            />
                            <button
                                onClick={() => toggleRow(invoice.invoice_id)}
                                className="bg-gray-100 border border-gray-300 hover:bg-gray-200 p-2 rounded-full flex items-center justify-center transition-all w-8 h-8 shrink-0"
                                title={expandedRows[invoice.invoice_id] ? 'Hide Details' : 'Show Details'}
                            >
                                {expandedRows[invoice.invoice_id] ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
                            </button>
                            <div className="flex-1 text-sm">
                                <div className="space-y-1">
                                    <div>
                                        <strong className="text-foreground">Invoice Name:</strong> {invoice.name}
                                    </div>
                                    <div>
                                        <strong className="text-foreground">Issue Date:</strong> {formatDate(invoice.issue_date)}
                                        {' '}<strong className="text-foreground ml-4">Due Date:</strong> {formatDate(invoice.due_date)}
                                    </div>
                                    <div>
                                        <strong className="text-foreground">Payment Instructions:</strong> {invoice.payment_instruction}
                                    </div>
                                </div>

                                <div className="mt-3 text-gray-700">
                                    <div>
                                        <strong>Subtotal (Before Tax):</strong> ${formatCurrency(Number(invoice.sub_total_sum_lineitems).toFixed(2))}
                                        {' '}<strong>Tax Rate:</strong> {Number(invoice.tax_rate).toFixed(2)}%
                                        {' '}<strong>Tax Amount:</strong> ${formatCurrency(Number(invoice.tax_amount).toFixed(2))}
                                        {discountAmount > 0 && (
                                            <>
                                                {' '}<strong>Discount:</strong> ${formatCurrency(discountAmount.toFixed(2))}
                                            </>
                                        )}
                                    </div>
                                    <div className="mt-1">
                                        <strong>Total Amount (Subtotal + Tax - Discount):</strong> ${formatCurrency(Number(invoice.final_total).toFixed(2))}
                                        {' '}<strong>Payment Received:</strong> ${formatCurrency(Number(invoice.amount_paid).toFixed(2))}
                                        {' '}<strong>Balance Due:</strong> <span className={balanceDue > 0 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                                            ${formatCurrency(balanceDue.toFixed(2))}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Expanded Details - Itemized Invoices */}
                        {/* ItemizedInvoicesView fetches its own data when expanded - lazy loading */}
                        {expandedRows[invoice.invoice_id] && (
                            <div className="p-2 bg-white">
                                <ItemizedInvoicesView invoiceId={invoice.invoice_id} />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default InvoicesTableView;